import { QRCause, QRDepartureRestrictions, QRExtRestriction, QRForeignPasport, QRGender, QRIdentityDocument, QRIdentityDocumentType, QRJobRecord, QRLocationAddress, QRMailingInfo, QRNameChange, QRPeriod, QRPerson, QRPersonAddress, QRReceptionPoint, QRReq, QRResidenceAddress, QRSourceInfo, _FullName, _IdentityDocument, _Person, _PersonIdentity } from 'types';
import { ApplicantIdentityDocument, ApplicantIdentityDocumentType, Gender, RepresentativeIdentityDocument, RepresentativeIdentityDocumentType } from '../features';
import { RequestFeaturesV2, RequestCauseV2, IndependentChildDocumentTypeV2, PersonAddressV2, PermAddressV2, TempAddressV2, ActiveFPassportInfoV2, RepresentativeProofDocumentV2, RepresentativeProofDocumentTypeV2 } from '../features2';
import { DepartmentInfo, VersionInfo } from './qr-code.converter';

export class RequestToQrCodeStringConverter2 {
    private printableDate(date: string | null): string | null {
        if (date === null) {
            return null;
        }
        return date.substr(8, 2) + '.' + date.substr(5, 2) + '.' + date.substr(0, 4);
    }

    private toQRIdentityDocument(doc: ApplicantIdentityDocument | RepresentativeIdentityDocument | RepresentativeProofDocumentV2): QRIdentityDocument {
        const res = new QRIdentityDocument();
        res.IssueDate = doc.issueDate?.value;
        res.Issuer = doc.issuer;
        res.Number = doc.number;
        res.Series = doc.series;
        res.Type = new QRIdentityDocumentType();
        switch (doc.type) {
            case RepresentativeIdentityDocumentType.Custom:
                res.Type.Id = 0;
                res.Type.Text = "ПРОИЗВОЛЬНЫЙ ДОКУМЕНТ";
                break;
            case ApplicantIdentityDocumentType.BirthCertificate:
            case RepresentativeProofDocumentTypeV2.BirthCertificate:
                res.Type.Id = 1;
                res.Type.Text = "СВИДЕТЕЛЬСТВО О РОЖДЕНИИ";
                break;
            case ApplicantIdentityDocumentType.RussianPassport:
            case RepresentativeIdentityDocumentType.RussianPassport:
                res.Type.Id = 3;
                res.Type.Text = "РОССИЙСКИЙ ПАСПОРТ";
                break;
            case RepresentativeIdentityDocumentType.RussianForeignPassport:
                res.Type.Id = 4;
                res.Type.Text = "РОССИЙСКИЙ ЗАГРАНИЧНЫЙ ПАСПОРТ";
                break;
            case ApplicantIdentityDocumentType.NoDocument:
                res.Type.Id = 5;
                res.Type.Text = "ДОКУМЕНТ ОТСУТСТВУЕТ";
                break;
            case RepresentativeProofDocumentTypeV2.Trustee:
                res.Type.Id = 6;
                res.Type.Text = "ДОКУМЕНТ, ПОДТВЕРЖДАЮЩИЙ ОПЕКУНСТВО ИЛИ ПОПЕЧИТЕЛЬСТВО";
                break;
            case IndependentChildDocumentTypeV2.SelfSufficientCourtDecision:
                res.Type.Id = 7;
                res.Type.Text = "РЕШЕНИЕ СУДА (ДЕЕСПОСОБНЫЙ РЕБЕНОК)";
                break;
            case IndependentChildDocumentTypeV2.SelfSufficientMarriageCertificate:
                res.Type.Id = 8;
                res.Type.Text = "СВИДЕТЕЛЬСТВО О БРАКЕ (ДЕЕСПОСОБНЫЙ РЕБЕНОК)";
                break;
            case IndependentChildDocumentTypeV2.SelfSufficientGuardianDecision:
                res.Type.Id = 9;
                res.Type.Text = "РЕШЕНИЕ ОРГАНОВ ОПЕКИ (ДЕЕСПОСОБНЫЙ РЕБЕНОК)";
                break;
        }
        return res;
    }

    private toQRAddress(addr: PersonAddressV2): QRPersonAddress {
        const res = new QRPersonAddress();
        res.Country = addr.country;
        res.AdmDivision = addr.admDivision.value || '';
        res.Region = addr.region.value || '';
        res.Settlement = addr.settlement;
        res.Street = addr.street.value || '';
        res.Building = addr.building.value || '';
        res.Housing = addr.housing.value || '';
        res.Structure = addr.structure.value || '';
        res.Flat = addr.flat.value || '';
        return res;
    }

    private toQRPermAddress(addr: PermAddressV2): QRResidenceAddress {
        const res = new QRResidenceAddress();
        res.Address = this.toQRAddress(addr.address);
        res.Date = addr.from.value;
        return res;
    }

    private toQRTempAddress(addr: TempAddressV2): QRLocationAddress {
        const res = new QRLocationAddress();
        res.Address = this.toQRAddress(addr.address);
        res.Period = new QRPeriod();
        res.Period.From = addr.from.value;
        res.Period.To = addr.to.value || '';
        res.IsReal = addr.type.val() === '1';
        return res;
    }

    private toQRFPassport(p: ActiveFPassportInfoV2): QRForeignPasport {
        const res = new QRForeignPasport();
        res.Series = p.series;
        res.Number = p.number;
        res.IssueDate = p.issueDate.value;
        res.Issuer = p.issuer;
        res.MustBeCanceled = p.mustBeCanceled || false;
        res.Lost = p.lost || false;
        return res;
    }

    private toQRGender(g: Gender): QRGender {
        const res = new QRGender();
        if (g === Gender.Male) {
            res.Id = 0;
            res.Text = "МУЖСКОЙ";
        } else {
            res.Id = 1;
            res.Text = "ЖЕНСКИЙ";
        }
        return res;
    }

    toQRReq(r: RequestFeaturesV2, department: DepartmentInfo, version: VersionInfo, aDate: string | undefined): QRReq {
        const res = new QRReq();
        res.Application.Person.Gender = this.toQRGender(r.applicant.gender);
        res.Application.Person.LastName = r.applicant.personIdentity.fullName.lastName;
        res.Application.Person.FirstName = r.applicant.personIdentity.fullName.firstName;
        res.Application.Person.MiddleName = r.applicant.personIdentity.fullName.middleName.value || '';
        res.Application.Person.LastNameLat = r.applicant.lastNameLat;
        res.Application.Person.FirstNameLat = r.applicant.firstNameLat;
        res.Application.Person.Birthdate = r.applicant.personIdentity.birthDate.value.replace('-00-00', '-01-01');
        res.Application.Person.BirthDateCompleteness = r.applicant.personIdentity.birthDate.value.endsWith('-00-00') ? 1 : 4;
        res.Application.Person.BirthPlaceFull = r.applicant.birthPlaceFull;
        res.Application.Person.BirthPlaceRus = r.applicant.birthPlaceRus;
        res.Application.Person.BirthPlaceLat = r.applicant.birthPlaceLat;
        res.Application.Person.Phone = r.applicant.phone;
        res.Application.Person.Email = r.applicant.email;
        res.Incapable = !!r.incapable;
        res.IndependentChild = !!r.independentChild;
        res.Application.Person.IdentityDocument = this.toQRIdentityDocument(r.applicant.identityDocument);
        if (r.independentChildDocument) {
            res.Application.IndependedChildDocument = new _IdentityDocument();
            res.Application.IndependedChildDocument.Type = +(r.independentChildDocument.type.val());
            res.Application.IndependedChildDocument.Series = r.independentChildDocument.series;
            res.Application.IndependedChildDocument.Number = r.independentChildDocument.number;
            res.Application.IndependedChildDocument.Issuer = r.independentChildDocument.issuer;
            res.Application.IndependedChildDocument.IssueDate = r.independentChildDocument.issueDate.value;
        }
        res.Application.Person.ResidenceAddress = this.toQRPermAddress(r.applicant.permAddress);
        res.Application.Person.LocationAddress = this.toQRTempAddress(r.applicant.tempAddress);
        if (r.applicant.nameChanges.length) {
            res.Application.Person.NameChanges = [];
            r.applicant.nameChanges.forEach(n => {
                const qrN = new QRNameChange();
                qrN.FirstName = n.firstName;
                qrN.LastName = n.lastName;
                qrN.MiddleName = n.middleName.value || '';
                qrN.Location = n.location;
                const nameChangeDateStr = n.year?.value.split('-00').join('-01');
                if (nameChangeDateStr.length < 10) {
                    qrN.ChangeDate = nameChangeDateStr.substr(0, 4) + '-01-01';
                } else {
                    qrN.ChangeDate = nameChangeDateStr;
                }
                res.Application.Person.NameChanges!.push(qrN);
            })
        }
        if (r.applicant.jobRecords && r.applicant.jobRecords.length) {
            res.Application.Person.JobHistory = [];
            r.applicant.jobRecords.forEach(r => {
                let jr = new QRJobRecord();
                jr.Job = r.position;
                jr.Address = r.address;
                jr.DateFrom = r.from.value.replace('-00', '-01');
                jr.DateTo = r.to.value ? r.to.value.replace('-00', '-01') : undefined;
                res.Application.Person.JobHistory!.push(jr);
            });
            if (r.applicant.jobManDate) {
                res.Application.JobDate = r.applicant.jobManDate.value;
                res.Application.JobMan = r.applicant.jobManName;
            }
        } else {
            res.Application.Person.JobHistory = []; // 4.6.19 compatibility
        }
        res.Application.Cause = new QRCause();
        switch (r.cause) {
            case RequestCauseV2.FirstTime:
                res.Application.Cause.Id = 0;
                res.Application.Cause.Text = "ПЕРВИЧНОЕ";
                break;
            case RequestCauseV2.InsteadOfLostPassport:
                res.Application.Cause.Id = 3;
                res.Application.Cause.Text = "ВЗАМЕН УТРАЧЕННОГО";
                break;
            case RequestCauseV2.InsteadOfUsedOrSpoiltPassport:
                res.Application.Cause.Id = 6;
                res.Application.Cause.Text = "ВЗАМЕН ИСПОЛЬЗОВАННОГО/ИСПОРЧЕННОГО";
                break;
            case RequestCauseV2.InsteadOfActive:
                res.Application.Cause.Id = 7;
                res.Application.Cause.Text = "ВЗАМЕН ДЕЙСТВУЮЩЕГО";
                break;
            case RequestCauseV2.AdditionToActive:
                res.Application.Cause.Id = 8;
                res.Application.Cause.Text = "В ДОПОЛНЕНИЕ К ДЕЙСТВУЮЩЕМУ";
                break;
        }
        if (r.applicant.activeFPassports.p1) {
            res.Application.Person.ForeignPassports = [];
            res.Application.Person.ForeignPassports.push(this.toQRFPassport(r.applicant.activeFPassports.p1));
            if (r.applicant.activeFPassports.p2) {
                res.Application.Person.ForeignPassports.push(this.toQRFPassport(r.applicant.activeFPassports.p2));
            }
        }
        if (r.secretAccess) {
            res.Application.DepartureRestrictions = new QRDepartureRestrictions();
            if (r.secretAccess.value.val() === '1') {
                res.Application.DepartureRestrictions.SecretAccess = new QRExtRestriction();
                res.Application.DepartureRestrictions.SecretAccess.Organization = r.secretAccess.organization;
                res.Application.DepartureRestrictions.SecretAccess.Date = r.secretAccess.year.value.substr(0, 4) + '-01-01';
            }
            if (r.contractObligations.value.val() === '1') {
                res.Application.DepartureRestrictions.ContractObligation = new QRExtRestriction();
                res.Application.DepartureRestrictions.ContractObligation.Organization = r.contractObligations.organization;
                res.Application.DepartureRestrictions.ContractObligation.Date = r.contractObligations.year.value.substr(0, 4) + '-01-01';
            }
            res.Application.DepartureRestrictions.MilitaryService = r.militaryService.val() === '1';
            res.Application.DepartureRestrictions.Convicted = r.convicted.val() === '1';
            res.Application.DepartureRestrictions.Accused = r.accused.val() === '1';
            res.Application.DepartureRestrictions.EvadingLaw = r.legalObligations.val() === '1';
            res.Application.DepartureRestrictions.Bankrupt = r.bankrupt.val() === '1';
        }
        if (r.representative) {
            res.Application.ChildRepresentative = new QRPerson();
            res.Application.ChildRepresentative.Gender = this.toQRGender(r.representative.gender);
            res.Application.ChildRepresentative.LastName = r.representative.personIdentity.fullName.lastName;
            res.Application.ChildRepresentative.FirstName = r.representative.personIdentity.fullName.firstName;
            res.Application.ChildRepresentative.MiddleName = r.representative.personIdentity.fullName.middleName.value || '';
            res.Application.ChildRepresentative.Birthdate = r.representative.personIdentity.birthDate.value.replace('-00-00', '-01-01');
            res.Application.ChildRepresentative.BirthDateCompleteness = r.representative.personIdentity.birthDate.value.endsWith('-00-00') ? 1 : 4;
            res.Application.ChildRepresentative.BirthPlaceRus = r.representative.birthPlace;
            res.Application.ChildRepresentative.Phone = r.representative.phone;
            res.Application.ChildRepresentative.Email = r.representative.email;
            res.Application.ChildRepresentative.ResidenceAddress = this.toQRPermAddress(r.representative.permAddress);
            res.Application.ChildRepresentative.LocationAddress = this.toQRTempAddress(r.representative.tempAddress);
            res.Application.ChildRepresentative.IdentityDocument = this.toQRIdentityDocument(r.representative.identityDocument);
            res.Application.ChildRepresentative.RepresentativeProof = this.toQRIdentityDocument(r.representative.representativeProof);
            if (r.representative.trustRecipient.active) {
                res.Application.ChildRepresentative.TrustRecepient = new _Person();
                res.Application.ChildRepresentative.TrustRecepient.Gender = Number(r.representative.trustRecipient.gender.val());
                res.Application.ChildRepresentative.TrustRecepient.BirthPlace = r.representative.trustRecipient.birthPlace;
                res.Application.ChildRepresentative.TrustRecepient.PersonIdentity = new _PersonIdentity();
                res.Application.ChildRepresentative.TrustRecepient.PersonIdentity.BirthDate = r.representative.trustRecipient.personIdentity.birthDate.value.replace('-00-00', '-01-01');
                res.Application.ChildRepresentative.TrustRecepient.PersonIdentity.PrintBirthDate = this.printableDate(r.representative.trustRecipient.personIdentity.birthDate.value) || undefined;
                res.Application.ChildRepresentative.TrustRecepient.PersonIdentity.FullName = new _FullName();
                res.Application.ChildRepresentative.TrustRecepient.PersonIdentity.FullName.LastName = r.representative.trustRecipient.personIdentity.fullName.lastName;
                res.Application.ChildRepresentative.TrustRecepient.PersonIdentity.FullName.FirstName = r.representative.trustRecipient.personIdentity.fullName.firstName;
                res.Application.ChildRepresentative.TrustRecepient.PersonIdentity.FullName.MiddleName = r.representative.trustRecipient.personIdentity.fullName.middleName.value || '';
            }
        }
        if (r.mailing && r.mailing.active) {
            res.MailPass = new QRMailingInfo();
            res.MailPass.Active = true;
            res.MailPass.AddressLat = r.mailing.addressLat;
            res.MailPass.AddressRus = r.mailing.addressRus;
        }
        if (r.mobileOptions) {
            res.ReceptionPoint = new QRReceptionPoint();
            res.ReceptionPoint.Code = r.mobileOptions.department;
            res.ReceptionPoint.Country = department.country;
            res.ReceptionPoint.City = department.city;
        }
        res.Application.ApplicantDate = aDate;
        res.Application.SourceInfo = new QRSourceInfo();
        res.Application.SourceInfo.WebSiteVersion = version.website;
        return res;
    }
}
